import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Button, Dialog, Hint, Icon, Input, SelectableNFTList, } from '@/components';
import { useCheckIsOwned, useERC721Balance, useGetERC721TokensByIds } from '@/hooks';
import { useBoolean } from '@/utils';
import classnames from 'classnames';
import { uniq } from 'lodash-es';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocales } from '../useLocales';
import styles from './index.module.less';
import { Search } from './Search';
const limit = 1000;
export const NFTPickerDialog = ({ tokens, pendingSize = 0, contractAddress, contractName, selectedTokenIds = [], onConfirm, loading, onClose, ...rest }) => {
    const t = useLocales();
    const balance = useERC721Balance(contractAddress);
    const [keyword, setKeyword] = useState('');
    const [isSearching, setIsSearching, setNotSearching] = useBoolean();
    const [searchVisible, openSearch, closeSearch] = useBoolean();
    const [pickedIds, setPickedIds] = useState(selectedTokenIds);
    const [notOwnedIds, setNotOwnedIds] = useState([]);
    const getERC721TokensByIds = useGetERC721TokensByIds(contractAddress);
    const [searchedTokens, setSearchedTokens] = useState([]);
    useEffect(() => {
        setPickedIds(selectedTokenIds);
    }, [selectedTokenIds]);
    useEffect(() => {
        if (!keyword.trim().length) {
            closeSearch();
        }
    }, [keyword]);
    const tokenIds = useMemo(() => {
        const seps = keyword.split(',');
        return seps.map((id) => id.trim()).filter(Boolean);
    }, [keyword]);
    const checkIsOwned = useCheckIsOwned(contractAddress);
    const search = useCallback(async (evt) => {
        evt.preventDefault();
        if (!keyword || tokenIds.length === 0)
            return;
        openSearch();
        setIsSearching();
        const [tokens, isOwnedFlags] = await Promise.all([
            getERC721TokensByIds(tokenIds),
            Promise.all(tokenIds.map((id) => checkIsOwned(id))),
        ]);
        const ids = tokens.filter((_, index) => !isOwnedFlags[index]).map((t) => t.tokenId);
        setNotOwnedIds(ids);
        setSearchedTokens(tokens.filter((_, index) => isOwnedFlags[index]));
        setNotSearching();
    }, [tokenIds, getERC721TokensByIds]);
    return (_jsxs(Dialog, { ...rest, title: t('Select your collection'), onClose: () => {
            onClose?.();
            setPickedIds(selectedTokenIds);
        }, children: [_jsx("form", { onSubmit: search, children: _jsxs("div", { className: styles.searchGroup, children: [_jsx(Input, { fullWidth: true, value: keyword, className: styles.input, placeholder: t('Token ID separated by comma, e.g. 23453, 2565'), onChange: (evt) => setKeyword(evt.currentTarget.value), leftAddon: _jsx(Icon, { type: "search", size: 24 }) }), _jsx(Button, { round: false, disabled: !keyword, type: "submit", colorScheme: "primary", children: t('Search') })] }) }), searchVisible ? (_jsx(Search, { tokens: searchedTokens, notOwnedIds: notOwnedIds, limit: limit, loading: isSearching, selectedTokenIds: pickedIds, onConfirm: (ids) => {
                    if (ids.join() !== pickedIds.join()) {
                        setPickedIds(uniq([...pickedIds, ...ids]));
                    }
                    closeSearch();
                } })) : null, _jsxs("div", { style: { display: searchVisible ? 'none' : 'block' }, children: [_jsx(SelectableNFTList, { tokens: tokens, contractName: contractName, pendingSize: pendingSize, loading: loading, limit: limit, selectedTokenIds: pickedIds, onChange: setPickedIds, className: styles.selectList }), _jsxs("div", { className: styles.summary, children: [pickedIds.length >= limit && (_jsx("span", { className: styles.warning, children: t('The mystery box contract supports up to {limit} NFTs for sale.', { limit }) })), _jsx("span", { className: classnames(styles.picked, styles.error), children: pickedIds.length }), " /", ' ', _jsx("span", { className: styles.total, children: balance.toString() }), _jsx(Hint, { width: 256, position: "right", children: t('The maximum number of NFTs to be sold in one mystery box contract is {limit}.', {
                                    limit,
                                }) })] }), _jsx("div", { className: styles.buttonGroup, children: _jsx(Button, { fullWidth: true, onClick: () => onConfirm?.(pickedIds), colorScheme: "primary", children: t('Confirm') }) })] })] }));
};
